import { z } from 'zod'
import { optionalString, requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Flujo de trabajo (secuencia ordenada de estados por acción) — fuente única
 * de verdad para la validación del API (nestjs-zod `createZodDto`) y del
 * formulario web (RHF + zodResolver).
 *
 * `name` es único GLOBAL (la UI y el deep-link identifican al flujo por
 * nombre). Reglas de dominio en el service: la acción `new` es EXCLUSIVA (no
 * se combina con otras), hay una sola `default` por acción (marcar una
 * desmarca la anterior) y la default no cambia de acción ni se elimina. El
 * `order` de los estados lo recalcula SIEMPRE el servidor a partir de la
 * posición en el array.
 */

export const WORKFLOW_ACTION = {
  /** Alta de una negociación nueva. */
  NEW: 'new'
} as const
export type WorkflowAction = (typeof WORKFLOW_ACTION)[keyof typeof WORKFLOW_ACTION]

// ObjectId de Mongo serializado (24 hex): el wire actual referencia estados
// por ObjectId; no cambiar hasta contrato v2.
const objectIdString = z
  .string({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
  .regex(/^[a-f0-9]{24}$/i, 'Identificador inválido')

/** Paso del flujo. El cliente puede mandar `order`, pero el server lo recalcula. */
export const WorkflowStateInputSchema = z.object({
  state: objectIdString,
  order: z.number().int().min(1).optional()
})

const WorkflowInputBaseSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  states: z
    .array(WorkflowStateInputSchema, { required_error: MESSAGES.REQUIRED })
    .min(1, 'Agrega al menos un estado al flujo'),
  actions: z
    .array(z.nativeEnum(WORKFLOW_ACTION), {
      required_error: MESSAGES.REQUIRED,
      invalid_type_error: MESSAGES.REQUIRED
    })
    .min(1, MESSAGES.REQUIRED),
  default: z.boolean().optional(),
  active: z.boolean().optional()
})

export const CreateWorkflowInputSchema = WorkflowInputBaseSchema

export const UpdateWorkflowInputSchema = WorkflowInputBaseSchema.partial()

export type WorkflowStateInput = z.infer<typeof WorkflowStateInputSchema>
export type CreateWorkflowInput = z.infer<typeof CreateWorkflowInputSchema>
export type UpdateWorkflowInput = z.infer<typeof UpdateWorkflowInputSchema>
